import Full from '../shared/Full';
import { useCallback, useEffect, useState } from 'react';
import { AppDispatch } from '../core/store';
import Splash from '../shared/components/splash/Splash';
import { redirectToMsiMainWebsite, redirectToSignIn } from '../utils/helpers';
import { useAppDispatch, useAppSelector } from '../core/rootHooks';
import ModalManager from '../shared/components/modal/ModalManager';
import { getUserInfoRequest } from './appSlice';
import { selectError, selectIsAuthorized } from './appSelectors';
import { useAuth } from '../hooks/useAuth';
import EnvironmentInfoSnackbar from '../shared/components/notifications/EnvironmentInfoSnackbar';
import { isNil, isEmpty } from 'lodash';
import { trackEvent, trackExceptionWithContext } from '../services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from '../services/application-insights/appInsightsConstants';
import { useRateLimitViolation } from '../hooks/useRateLimitViolation';
import { isRateLimited, setRateLimit, showRateLimitOverlay } from '../utils/rateLimitHelper';
import AppEntryVerification from '../shared/components/app-entry-verification/AppEntryVerification';

function App() {
	const dispatch = useAppDispatch<AppDispatch>();

	const { isAuthenticated, isLoading } = useAuth();
	const { isUserLimitedEndTime, isCheckingRateLimit } = useRateLimitViolation();

	const isAuthorized = useAppSelector(selectIsAuthorized);
	const error = useAppSelector(selectError);

	const [isHumanVerified, setIsHumanVerified] = useState(false);

	const getUserInfo = useCallback(async () => {
		try {
			// User info fetch attempt is already tracked in authApi.ts
			dispatch(getUserInfoRequest());
		} catch (error) {
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.USER_MANAGEMENT_APP,
				action: 'get-user-info-callback-failed',
				additionalData: {
					errorType: AppInsightsErrorTypes.USER_MANAGEMENT_ERROR,
					isAuthenticated,
				},
			});
		}
	}, [dispatch, isAuthenticated]);

	const handleHumanVerified = () => {
		setIsHumanVerified(true);
	};

	useEffect(() => {
		if (isAuthenticated) {
			getUserInfo();
		}
	}, [getUserInfo, isAuthenticated]);

	const isNullOrEmpty = (str: string) => isNil(str) || isEmpty(str);
	if (error) {
		trackExceptionWithContext(new Error(error), {
			component: AppInsightsComponents.USER_MANAGEMENT_APP,
			action: 'user-error-modal-shown',
			additionalData: {
				errorType: AppInsightsErrorTypes.USER_AUTHENTICATION_ERROR,
				errorMessage: error,
				isAuthenticated,
				isAuthorized,
			},
		});

		ModalManager.show({
			size: 'md',
			hideHeaderCloseIcon: true,
			header: 'Warning',
			message: error,
			actionButtonText: 'Continue',
			onConfirm: () => {
				trackEvent(AppInsightsActions.USER_AUTHORIZATION_FAILURE, {
					component: AppInsightsComponents.USER_MANAGEMENT_APP,
					action: 'redirect-to-signin-from-error',
					errorMessage: error,
				});
				redirectToSignIn();
			},
			onCancel: () => {
				trackEvent(AppInsightsActions.USER_AUTHORIZATION_FAILURE, {
					component: AppInsightsComponents.USER_MANAGEMENT_APP,
					action: 'redirect-to-main-website-from-error',
					errorMessage: error,
				});
				redirectToMsiMainWebsite();
			},
		});
	}

	if (!isLoading && !isAuthenticated) {
		trackEvent(AppInsightsActions.USER_AUTHORIZATION_FAILURE, {
			component: AppInsightsComponents.USER_MANAGEMENT_APP,
			action: 'redirect-to-signin-unauthenticated',
			isLoading,
			isAuthenticated,
		});
		redirectToSignIn();
	}

	if (isRateLimited() || isCheckingRateLimit) {
		return null;
	}

	if (isUserLimitedEndTime) {
		setRateLimit(isUserLimitedEndTime);
		showRateLimitOverlay();
		return null;
	}

	return (
		<>
			{!isNullOrEmpty(window.config.ENVIRONMENT_NAME) && <EnvironmentInfoSnackbar />}
			{
				// Render Splash screen while loading or render Full screen if authorized
				(isLoading && <Splash />) ||
					(isAuthorized && (
						<AppEntryVerification onVerified={handleHumanVerified}>
							{isHumanVerified && (
								<>
									<Full />
								</>
							)}
						</AppEntryVerification>
					)) ||
					null
			}
		</>
	);
}

export default App;
